import { getLocaleTimezoneIsoDate, getPricePrecision } from '@skytech/utils'

import type {
  Customer,
  AssetExtended,
  Employee,
  UpdateAssetPayload,
} from '@/modules/admin/resources'
import type { LeasingFieldLocks } from '@/resources/leasing/types'

export const formattedCostValue = (value: number) => {
  if (isNaN(value)) return ''
  const precision = getPricePrecision(value)
  return value.toFixed(precision)
}

export function getInitialValues(assetDetails: AssetExtended, costObjectList: Employee[]) {
  const costObject = costObjectList.find(costObject => costObject.id === assetDetails.costObjectId)
  return {
    userName: (costObject && `${costObject.name} - ${costObject.code}`) || '',
    costObjectId: assetDetails.costObjectId,
    customerAddressId: assetDetails.customerAddressId || '',
    comment: assetDetails.comment || '',
    cost: assetDetails.cost ? formattedCostValue(assetDetails.cost) : '',
    customerId: assetDetails.customerId,
    assetDescription: assetDetails.assetDescription || '',
    imei: assetDetails.imei || '',
    snr: assetDetails.snr || '',
    ownershipTypeId: assetDetails.ownershipTypeId,
    received: assetDetails.received ? getLocaleTimezoneIsoDate(assetDetails.received) : '',
    statusId: assetDetails.statusId,
    typeId: assetDetails.typeId,
    userTypeId: assetDetails.userTypeId,
    leaseStart: assetDetails.leaseStart ? getLocaleTimezoneIsoDate(assetDetails.leaseStart) : '',
    leaseEnd: assetDetails.leaseEnd ? getLocaleTimezoneIsoDate(assetDetails.leaseEnd) : '',
    ecomPolicyId: assetDetails.policyId,
    conditionDesc: assetDetails.conditionDesc || '',
  }
}

export const getSelectedCustomerId = (customerName: string, customers?: Customer[]) =>
  customers?.find(customer => `${customer.name} ${customer.orgNo}` === customerName)?.id

export const getCostObjectById = (costObjectList: Employee[], costObjectId: number) =>
  costObjectList.find(costObject => costObject.id === costObjectId)

export const mapPayloadWithLockedFields = ({
  formValues,
  assetStatusId,
  locked,
}: {
  formValues: UpdateAssetPayload
  assetStatusId: number | string
  locked: { leasingFieldLocks?: LeasingFieldLocks; lockedFields?: Array<string> }
}) => {
  const { leasingFieldLocks, lockedFields } = locked
  const payload: UpdateAssetPayload = {
    ...formValues,
    costObjectId: !(leasingFieldLocks?.assetUser || lockedFields?.includes('assetUser'))
      ? formValues.costObjectId
      : null,
    userName: !(leasingFieldLocks?.assetUser || lockedFields?.includes('assetUser'))
      ? formValues.userName
      : null,
    customerId: !(leasingFieldLocks?.assetCustomer || lockedFields?.includes('customerId'))
      ? formValues.customerId
      : null,
    statusId: !(leasingFieldLocks?.assetStatus || lockedFields?.includes('statusId'))
      ? formValues.statusId
      : assetStatusId,
    typeId: !(leasingFieldLocks?.assetType || lockedFields?.includes('typeId'))
      ? formValues.typeId
      : null,
    ownershipTypeId: !(leasingFieldLocks?.assetOwnership || lockedFields?.includes('ownershipId'))
      ? formValues.ownershipTypeId
      : null,
    assetDescription: !(
      leasingFieldLocks?.assetDescription || lockedFields?.includes('assetDescription')
    )
      ? formValues.assetDescription
      : null,
    imei: !(leasingFieldLocks?.imeiSerialNumber || lockedFields?.includes('imeiSerialNumber'))
      ? formValues.imei
      : null,
    snr: !(leasingFieldLocks?.imeiSerialNumber || lockedFields?.includes('imeiSerialNumber'))
      ? formValues.snr
      : null,
    leaseStart: !(leasingFieldLocks?.leaseStart || lockedFields?.includes('leaseStart'))
      ? formValues.leaseStart
      : null,
    leaseEnd: !(leasingFieldLocks?.leaseEnd || lockedFields?.includes('leaseEnd'))
      ? formValues.leaseEnd
      : null,
  }

  return payload
}
